import { Link } from "react-router-dom";
import { ArrowRight, Quote } from "lucide-react";
import { GhostQuote } from "@/components/Ghost";
import { FadeUp, Eyebrow } from "@/lib/motion";
import { TESTIMONIALS, shortName } from "@/data/testimonials";

const initials = (name) =>
  name
    .split(" ")
    .map((p) => p[0])
    .join("")
    .slice(0, 2);

export default function Testimonials() {
  return (
    <section
      id="results"
      data-testid="testimonials-section"
      className="relative overflow-hidden py-24 md:py-32"
    >
      <GhostQuote className="right-6 top-16 text-slate-900/[0.07] md:right-16" />
      <div className="mx-auto max-w-7xl px-4 sm:px-8 lg:px-12">
        <FadeUp>
          <div className="flex flex-wrap items-center gap-5">
            <Eyebrow>Proof</Eyebrow>
            <span
              data-testid="results-review-count-badge"
              className="inline-flex items-center gap-2 rounded-full border border-flame/30 bg-flame/10 px-4 py-1.5 font-mono text-base text-flame"
            >
              <span className="h-1.5 w-1.5 rounded-full bg-flame" />
              97 recommendations · since 2007
            </span>
          </div>
          <h2 className="mt-5 max-w-2xl font-display text-4xl font-bold leading-[1.02] tracking-tight text-slate-900 sm:text-5xl">
            Client testimonials —{" "}
            <span className="text-flame">and said so.</span>
          </h2>
        </FadeUp>

        <div
          data-testid="testimonials-grid"
          className="mt-14 grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-3 md:gap-6"
        >
          {TESTIMONIALS.map((t, i) => (
            <FadeUp key={t.name} delay={(i % 3) * 0.1} className="h-full">
              <figure
                data-testid={`testimonial-card-${i + 1}`}
                className="glass flex h-full flex-col rounded-2xl border border-slate-200 p-7 transition-all duration-500 hover:-translate-y-1 hover:border-flame/40"
              >
                <Quote className="h-6 w-6 text-flame" />
                <blockquote className="mt-5 flex-1 text-lg leading-relaxed text-slate-800">
                  {t.quote}
                </blockquote>
                <figcaption className="mt-7 flex items-center gap-4 border-t border-slate-200 pt-5">
                  <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-flame/40 bg-flame/10 font-mono text-base font-semibold text-flame">
                    {initials(t.name)}
                  </span>
                  <div>
                    <div className="font-semibold text-slate-900">{shortName(t.name)}</div>
                    <div className="mt-0.5 text-base leading-snug text-faint">
                      {t.role}
                    </div>
                  </div>
                </figcaption>
              </figure>
            </FadeUp>
          ))}
        </div>

        <FadeUp delay={0.1}>
          <div className="mt-12 flex justify-center">
            <Link
              to="/testimonials"
              data-testid="testimonials-page-link"
              className="group flex items-center gap-2 rounded-2xl border border-slate-300 px-6 py-3 font-mono text-base font-semibold uppercase tracking-[0.15em] text-slate-900 transition-all duration-300 hover:border-flame/60 hover:text-flame"
            >
              See all client testimonials
              <ArrowRight className="h-3.5 w-3.5 text-flame transition-transform duration-300 group-hover:translate-x-0.5" />
            </Link>
          </div>
        </FadeUp>
      </div>
    </section>
  );
}
